/**
 * `HarnessDetailDialog` — the magnifier-glass view for one harness row
 * in the Lexiun Agents pane. Mirrors `MissionDetailDialog` but for
 * standalone harness tasks (and, in v1.2, mission-leaf rows that may
 * eventually want their own drill-in).
 *
 * Opens via `useUIStore.openHarnessDetail(taskId)`; mounted exactly
 * once at the App-shell level.
 *
 * Plan: ai-docs/lexide-right-sidebar-implementation-plan.md §K-bis.
 * Backend contract: ai-docs/handoffs/team-agents-detail-dialog-backend-handoff-2026-06-22.md
 *
 * Phase-1 graceful-degradation: H3/H4/H5 (progress history, phase
 * timings, log tail) are NOT yet on the harn-service surface; this
 * dialog renders "—" for those blocks and notes the pending status.
 */
import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Pause,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { DetailDialogShell } from "./DetailDialogShell";
import { useHarnTasksPoll } from "@/hooks/useHarnTasksPoll";
import { useHarnTasksStore } from "@/stores/useHarnTasksStore";
import { useHarnessIndex } from "@/hooks/useHarnessIndex";
import { useUIStore } from "@/stores/useUIStore";
import { AwaitingQuestionsList } from "./AwaitingQuestionsList";
import { normalizeProgressPct } from "@/lib/agents/progress-pct";
import {
  getTask,
  type HarnTaskRecord,
  type HarnTaskStatus,
} from "@/api/lx-harn";

const TERMINAL_TASK_STATES: readonly HarnTaskStatus[] = [
  "completed",
  "failed",
];

export function HarnessDetailDialog() {
  const { t } = useTranslation();
  const taskId = useUIStore((s) => s.openHarnessDetailTaskId);
  const close = useUIStore((s) => s.closeDetailDialog);
  // Linked to the parent-mission affordance (ledger v2 missionId). Hook
  // MUST be called before any conditional early-return so the call order
  // stays stable across renders — React: "Rendered more hooks than during
  // the previous render."
  const openMissionDetail = useUIStore((s) => s.openMissionDetail);
  const slot = useHarnTasksStore((s) =>
    taskId ? s.tasks.get(taskId) : undefined,
  );
  const upsertTask = useHarnTasksStore((s) => s.upsertTask);
  const harnessIndex = useHarnessIndex();
  const { cancelTask } = useHarnTasksPoll();
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!taskId) {
      setFetchError(null);
      return;
    }
    let canceled = false;
    getTask(taskId)
      .then((rec) => {
        if (canceled) return;
        upsertTask(rec);
        setFetchError(null);
      })
      .catch((err) => {
        if (canceled) return;
        setFetchError(err instanceof Error ? err.message : "fetch failed");
      });
    return () => {
      canceled = true;
    };
  }, [taskId, upsertTask]);

  if (!taskId || !slot) return null;

  const record: HarnTaskRecord | undefined = slot.record;
  // Summary is the always-present base; record fills in additional fields.
  const summary = slot.summary;
  const rawStatus = (record?.status ?? summary.status) as HarnTaskStatus;
  // Per the ledger v2 handback: a user-cancelled harness on the LIVE
  // record reports `status:"failed"` + `error.code:"user_cancelled"`
  // (the central ledger normalizes to "canceled"). We surface
  // "canceled" on the dialog so the lawyer sees what they did, not a
  // misleading "failed". This is a DISPLAY override only — backend
  // shape unchanged.
  const wasUserCanceled =
    rawStatus === "failed" && record?.error?.code === "user_cancelled";
  const status: HarnTaskStatus | "canceled" = wasUserCanceled
    ? "canceled"
    : rawStatus;
  const isTerminal =
    status === "canceled" || TERMINAL_TASK_STATES.includes(rawStatus);
  const showCancel = !isTerminal && !slot.isCanceling;

  async function handleCancel() {
    if (!taskId) return;
    // i18n key `rightSidebar.confirmCancelHarness` uses `{{harnessId}}`
    // — pass the lawyer-facing display name (title → label → slug)
    // so the dialog shows "Cancel \"Jurisprudence Researcher\"?" not
    // "Cancel \"{{harnessId}}\"?".
    const cancelLabel =
      slot?.record?.title ??
      slot?.summary.title ??
      harnessIndex.get(slot?.summary.harnessId ?? "")?.tool.label ??
      slot?.summary.harnessId ??
      taskId;
    const confirmed = window.confirm(
      t("rightSidebar.confirmCancelHarness", { harnessId: cancelLabel }),
    );
    if (!confirmed) return;
    try {
      await cancelTask(taskId);
      close();
    } catch {
      /* error surfaces via store */
    }
  }

  // Per 2026-06-23 relay: `title` is the lawyer-facing HEADLINE; the
  // run-specific `summary` is the SUBTITLE/essence. The dialog used to
  // promote summary to the headline — flipped here so the harness's
  // friendly name leads ("Jurisprudence Researcher") with the run
  // essence below ("8 precedents found on jusbrasil").
  const manifest = harnessIndex.get(summary.harnessId);
  const taskTitle = record?.title ?? summary.title;
  const runSummary = record?.summary ?? summary.summary;
  const headline =
    taskTitle ?? manifest?.tool.label ?? summary.harnessId;
  // ⚠ manifest `tool.description` is DELIBERATELY NOT surfaced here.
  // Same category error as MissionDetailDialog: it is the LLM
  // TOOL-CALLING description (harn-sdk manifest schema.ts:52-56), English
  // and full of backticked identifiers and tool-choice advice. The
  // run-specific `summary` is the lawyer-facing text and already drives
  // the subtitle (tester-galvao finding 20260913T053839Z).
  // Mission linkage (ledger v2): when the task is a mission leaf, the
  // record carries the parent missionId. The footer surfaces a
  // "View parent mission" affordance the lawyer can click to drill up.
  // openMissionDetail was already grabbed at the top of the component
  // (hook ordering — required to be unconditional).
  const parentMissionId = record?.missionId ?? summary.missionId;

  return (
    <DetailDialogShell
      open={true}
      onClose={close}
      titleId="harness-detail-dialog-title"
      testid="harness-detail-dialog"
      header={
        <HarnessHeader
          titleId="harness-detail-dialog-title"
          status={status}
          harnessLabel={summary.harnessId}
          headline={headline}
          runSummary={runSummary}
          createdAt={record?.createdAt ?? summary.createdAt}
          startedAt={record?.startedAt}
          completedAt={record?.completedAt}
          durationMs={record?.durationMs}
          model={record?.model ?? summary.model}
          provider={record?.provider ?? summary.provider}
          costUsd={record?.costUsd ?? summary.costUsd}
          tokensIn={record?.tokensIn ?? summary.tokensIn}
          tokensOut={record?.tokensOut ?? summary.tokensOut}
        />
      }
      footer={
        <>
          {parentMissionId ? (
            <button
              type="button"
              data-testid="harness-detail-view-parent-mission"
              onClick={() => openMissionDetail(parentMissionId)}
              className="mr-auto rounded px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/40"
            >
              {t("detailDialog.viewParentMission")}
            </button>
          ) : null}
          {showCancel ? (
            <button
              type="button"
              data-testid="harness-detail-cancel"
              onClick={handleCancel}
              className="rounded bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-900/40"
            >
              {t("detailDialog.cancelHarness")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={close}
            data-testid="harness-detail-close-action"
            className="rounded bg-cream-200/60 px-3 py-1.5 text-xs font-medium text-coffee-700 hover:bg-cream-300/60 dark:bg-coffee-800 dark:text-slate-200 dark:hover:bg-coffee-700"
          >
            {t("detailDialog.close")}
          </button>
        </>
      }
    >
      {fetchError && !record ? (
        <p
          data-testid="harness-detail-fetch-error"
          className="rounded border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-300"
        >
          {t("detailDialog.fetchError", { error: fetchError })}
        </p>
      ) : null}

      {summary.progress ? (
        <HarnessProgressBlock
          step={summary.progress.step}
          pct={summary.progress.pct ?? 0}
          message={summary.progress.message}
          status={status}
        />
      ) : null}

      {record?.error ? <HarnessErrorBlock error={record.error} /> : null}

      {record?.awaitingUser ? (
        <HarnessAwaitingUserBlock
          questions={record.awaitingUser.questions}
          expiresAt={record.awaitingUser.expiresAt}
        />
      ) : null}

      <HarnessPhase3Pending />
    </DetailDialogShell>
  );
}

function HarnessHeader(props: {
  titleId: string;
  /** Includes the synthetic `"canceled"` value the dialog derives when
   *  the live record is `failed + user_cancelled`. */
  status: HarnTaskStatus | "canceled";
  /** Raw harness id (slug) — shown as a small uppercase chip next to
   *  the status badge so support has the canonical id at a glance. */
  harnessLabel: string;
  /** Lawyer-facing headline — task `title` falls through to
   *  manifest.tool.label or harnessId in the parent. */
  headline: string;
  /** Per-run essence — task `summary`. Subtitle below the headline. */
  runSummary?: string;
  /** Manifest description (what this kind of task does in general). */
  description?: string;
  createdAt: number;
  startedAt?: number;
  completedAt?: number;
  durationMs?: number;
  model?: string;
  provider?: string;
  costUsd?: number;
  tokensIn?: number;
  tokensOut?: number;
}) {
  const { t } = useTranslation();
  const elapsedMs =
    props.durationMs ??
    (props.startedAt && props.completedAt
      ? props.completedAt - props.startedAt
      : props.startedAt && !props.completedAt
        ? Date.now() - props.startedAt
        : undefined);
  const modelLabel =
    props.model && props.provider
      ? `${props.provider}/${props.model}`
      : (props.model ?? props.provider);
  return (
    <>
      <div className="mb-2 flex items-center gap-2">
        <HarnessStatusBadge status={props.status} />
        <span
          className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400 dark:text-slate-500"
          title={props.harnessLabel}
        >
          {t("detailDialog.taskLabel")}
        </span>
      </div>
      <h2
        id={props.titleId}
        className="text-lg font-medium leading-snug text-coffee-900 dark:text-cream-100"
      >
        {props.headline}
      </h2>
      {props.runSummary ? (
        <p
          data-testid="harness-detail-run-summary"
          className="mt-1.5 text-sm leading-snug text-coffee-700 dark:text-slate-200"
        >
          {props.runSummary}
        </p>
      ) : null}
      {props.description ? (
        <p
          data-testid="harness-detail-description"
          className="mt-1 text-xs italic leading-snug text-slate-500 dark:text-slate-400"
        >
          {props.description}
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
        {elapsedMs != null ? (
          <HeaderChip
            label={t("detailDialog.elapsed")}
            value={formatDurationMs(elapsedMs)}
            testid="harness-detail-header-elapsed"
          />
        ) : null}
        {modelLabel ? (
          <HeaderChip label={t("detailDialog.model")} value={modelLabel} />
        ) : null}
        {props.costUsd != null ? (
          <HeaderChip
            label={t("detailDialog.cost")}
            value={`$${props.costUsd.toFixed(4)} · ${props.tokensIn ?? 0}↑ ${props.tokensOut ?? 0}↓`}
            testid="harness-detail-cost"
          />
        ) : null}
        {/* Lifecycle timestamps — promoted to chips for visual
            consistency with the mission dialog. The plain timing
            line below the header was dropped (was redundant + did
            not match the modern card pattern). */}
        {props.startedAt ? (
          <HeaderChip
            label={t("detailDialog.startedAt")}
            value={formatAbsoluteWithSeconds(props.startedAt)}
            tooltip={formatAbsoluteWithSecondsLong(props.startedAt)}
          />
        ) : (
          <HeaderChip
            label={t("detailDialog.createdAt")}
            value={formatAbsoluteWithSeconds(props.createdAt)}
            tooltip={formatAbsoluteWithSecondsLong(props.createdAt)}
          />
        )}
        {props.completedAt ? (
          <HeaderChip
            label={t("detailDialog.completedAt")}
            value={formatAbsoluteWithSeconds(props.completedAt)}
            tooltip={formatAbsoluteWithSecondsLong(props.completedAt)}
          />
        ) : null}
      </div>
    </>
  );
}

function HarnessStatusBadge({
  status,
}: {
  status: HarnTaskStatus | "canceled";
}) {
  const { t } = useTranslation();
  const cls = "h-3 w-3";
  let icon: React.ReactNode;
  switch (status) {
    case "queued":
      icon = <Clock className={`${cls} text-slate-500`} />;
      break;
    case "running":
      icon = <Loader2 className={`${cls} animate-spin text-blue-500`} />;
      break;
    case "awaiting_user":
      icon = <Pause className={`${cls} text-amber-500`} />;
      break;
    case "completed":
      icon = <CheckCircle2 className={`${cls} text-emerald-500`} />;
      break;
    case "failed":
      icon = <XCircle className={`${cls} text-red-500`} />;
      break;
    case "canceled":
      icon = <XCircle className={`${cls} text-slate-400`} />;
      break;
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-cream-200/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-coffee-700 dark:bg-coffee-800/60 dark:text-slate-200">
      {icon}
      {t(`detailDialog.harnessStatus.${status}`)}
    </span>
  );
}

function HeaderChip(props: {
  label: string;
  value: string;
  tooltip?: string;
  testid?: string;
}) {
  return (
    <span
      data-testid={props.testid}
      title={props.tooltip ?? `${props.label}: ${props.value}`}
      className="inline-flex items-baseline gap-1 rounded-full bg-cream-100/70 px-2.5 py-1 tabular-nums text-coffee-700 dark:bg-coffee-800/40 dark:text-slate-300"
    >
      <span className="text-[9px] font-medium uppercase tracking-[0.08em] text-slate-400 dark:text-slate-500">
        {props.label}
      </span>
      <span>{props.value}</span>
    </span>
  );
}

function HarnessProgressBlock({
  step,
  pct,
  message,
  status,
}: {
  step: string;
  pct: number;
  message?: string;
  status: HarnTaskStatus | "canceled";
}) {
  const { t } = useTranslation();
  // pct from the wire can be 0..1 OR 0..100 (the harn-sdk schema is
  // intentionally lenient about which a harness emits) — this block's
  // original inline normaliser is now THE shared seam; see progress-pct.ts.
  const clamped = (normalizeProgressPct(pct) ?? 0) / 100;
  return (
    <section className="mb-4">
      <SectionHeading label={t("detailDialog.progress")} />
      <div
        data-testid="harness-detail-progress"
        className="rounded border border-cream-200/60 bg-cream-50/40 p-3 text-xs dark:border-coffee-700/60 dark:bg-coffee-950/30"
      >
        <div className="mb-1 flex items-baseline justify-between gap-2">
          <span className="truncate font-medium text-coffee-700 dark:text-slate-200">
            {step}
          </span>
          <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
            {Math.round(clamped * 100)}%
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-cream-200/60 dark:bg-coffee-800">
          <div
            data-testid="harness-detail-progress-bar"
            className={`h-1.5 rounded-full ${
              status === "running"
                ? "bg-blue-500"
                : status === "completed"
                  ? "bg-emerald-500"
                  : status === "failed"
                    ? "bg-red-500"
                    : "bg-amber-500"
            }`}
            style={{ width: `${clamped * 100}%` }}
          />
        </div>
        {message ? (
          <p className="mt-1 truncate text-slate-500 dark:text-slate-400">
            {message}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function HarnessErrorBlock({
  error,
}: {
  error: { code: string; message: string };
}) {
  const { t } = useTranslation();
  // SAFETY: never surface the raw `error.message` — it often references
  // platform internals (field names, file paths, stack snippets) that
  // are meaningless or confusing to a lawyer (operator 2026-06-23).
  // Render a friendly i18n label keyed by the error code, with a
  // documented `_default` fallback for any code we don't yet map.
  // The bare code slug is shown as a small chip for support tickets —
  // that's the only platform-internal token kept.
  const friendly = t(`detailDialog.errorCodes.${error.code}`, {
    defaultValue: t("detailDialog.errorCodes._default"),
  });
  return (
    <section
      data-testid="harness-detail-error"
      className="mb-4 rounded-lg border border-red-200/60 bg-red-50/60 p-3 text-xs dark:border-red-900/40 dark:bg-red-950/30"
    >
      <div className="flex items-start gap-2 font-medium text-red-700 dark:text-red-300">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>{friendly}</span>
      </div>
      <code
        data-testid="harness-detail-error-code"
        className="mt-1 inline-block rounded bg-red-100/70 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-red-700/80 dark:bg-red-900/40 dark:text-red-300/80"
        title={t("detailDialog.errorCodeTooltip")}
      >
        {error.code}
      </code>
    </section>
  );
}

function HarnessAwaitingUserBlock({
  questions,
  expiresAt,
}: {
  questions: unknown[];
  expiresAt: number;
}) {
  const { t } = useTranslation();
  return (
    <section
      data-testid="harness-detail-awaiting-user"
      className="mb-4 rounded border border-amber-300/60 bg-amber-50 p-3 text-xs dark:border-amber-700/60 dark:bg-amber-950/30"
    >
      <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-300">
        <Pause size={14} />
        {t("detailDialog.awaitingUserLabel")}
      </div>
      {/* Was a bare COUNT until 2026-08-17 — the list now carries the
          count line itself plus the question text, read-only. */}
      <AwaitingQuestionsList questions={questions} />
      <p className="mt-1 text-amber-800/70 dark:text-amber-300/70">
        {t("detailDialog.awaitingUserExpires", {
          when: new Date(expiresAt).toLocaleString(),
        })}
      </p>
    </section>
  );
}

function HarnessPhase3Pending() {
  const { t } = useTranslation();
  return (
    <section className="mb-1 text-[10px] italic text-slate-400 dark:text-slate-500">
      {t("detailDialog.phase3Pending")}
    </section>
  );
}

// ─── Shared bits ───────────────────────────────────────────────────────

function SectionHeading({ label }: { label: string }) {
  return (
    <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {label}
    </h3>
  );
}

// Forensic display: SECONDS matter (operator 2026-06-23). The dialog
// is a magnifier-glass view, so wall-clock precision wins over the
// chat-style "X minutes ago" relative form.

function formatDurationMs(ms: number): string {
  if (ms < 1_000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)} s`;
  if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)} m`;
  return `${(ms / 3_600_000).toFixed(1)} h`;
}

function formatAbsoluteWithSeconds(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hms = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  if (sameDay) return hms;
  const sameYear = d.getFullYear() === now.getFullYear();
  const dm = d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  return `${dm} ${hms}`;
}

function formatAbsoluteWithSecondsLong(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}
